import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { SupportAgent as SupportAgentIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import {
  PARTNER_SUPPORT_EVENT,
  endPartnerSupportSession,
  getPartnerSupportSession,
  isPartnerSupportSessionExpired,
} from '../utils/partnerSupportSession';

const EXPIRY_CHECK_MS = 30 * 1000;

const PartnerSupportBanner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [session, setSession] = useState(() => getPartnerSupportSession());
  const [expired, setExpired] = useState(() => isPartnerSupportSessionExpired(getPartnerSupportSession()));

  useEffect(() => {
    const refresh = () => {
      const current = getPartnerSupportSession();
      setSession(current);
      setExpired(isPartnerSupportSessionExpired(current));
    };
    window.addEventListener(PARTNER_SUPPORT_EVENT, refresh);
    window.addEventListener('storage', refresh);
    const timer = window.setInterval(refresh, EXPIRY_CHECK_MS);
    return () => {
      window.removeEventListener(PARTNER_SUPPORT_EVENT, refresh);
      window.removeEventListener('storage', refresh);
      window.clearInterval(timer);
    };
  }, []);

  const handleExit = useCallback(() => {
    endPartnerSupportSession();
    navigate('/admin');
  }, [navigate]);

  if (!session) return null;

  return (
    <Box
      role="status"
      data-testid="partner-support-banner"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.modal + 1,
        bgcolor: DOCUMENT.oxblood,
        color: DOCUMENT.ivory,
        px: { xs: 2, md: 3 },
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.2)',
      }}
    >
      <SupportAgentIcon fontSize="small" />
      <Typography sx={{ fontSize: 13.5, flex: 1, minWidth: 200 }}>
        {expired
          ? t('partnerSupport.bannerExpired', { company: session.companyName })
          : t('partnerSupport.banner', { company: session.companyName })}
      </Typography>
      <Button
        size="small"
        onClick={handleExit}
        data-testid="partner-support-exit"
        sx={{
          color: DOCUMENT.oxblood,
          bgcolor: DOCUMENT.ivory,
          borderRadius: 0,
          px: 2,
          '&:hover': { bgcolor: DOCUMENT.surface },
        }}
      >
        {t('partnerSupport.exit')}
      </Button>
    </Box>
  );
};

export default PartnerSupportBanner;
